import React, { forwardRef, Ref, memo, useMemo } from 'react'
import classNames from 'classnames'
import debounce from 'lodash/debounce'
import { Spinner } from 'transfix-ui/components/Spinner'
import uuid from 'uuid/v4'
import { IOption, IOptions, ISuggestionOption } from './types'
import { isOptionGroup } from './utils'

// debounced loading state
const debounceAmount: number = 1000
const showLoadingState = (isLoading: boolean, isOpen: boolean) => isLoading && isOpen
const debounceShowLoading = debounce(showLoadingState, debounceAmount)

// Option Component

interface IOptionProps {
  option: IOption
  optionType?: 'button' | 'checkbox'
  onOptionSelection(option: IOption): void
  dataTest?: string
}

const Option = memo<IOptionProps>(({ dataTest, onOptionSelection, option, optionType }) => {
  const { markup, text } = option

  const optionSelectHandler = () => onOptionSelection(option)

  const keyDownHandler = () => ({})

  // TODO: This is backwards-compatible with using
  // 'addOption' and I'm working to remove that functionality
  const classes = useMemo(
    () =>
      classNames('option', option.className, {
        'add-option': option.isAddOption,
      }),
    [option.isAddOption, option.className]
  )

  return (
    <div
      role='option'
      aria-selected='false'
      className={classes}
      onClick={optionSelectHandler}
      onKeyDown={keyDownHandler}
      tabIndex={-1}
      aria-label={text}
      data-test={`${dataTest}-${option.id || option.text}`}
      data-id={option.id}>
      {!optionType && <p aria-hidden='true'>{markup}</p>}
    </div>
  )
})

interface IOptionGroupProps {
  label: string
  groupKey: string
  groupOptions: IOption[]
  optionType?: 'button' | 'checkbox'
  onOptionSelection(option: IOption): void
  dataTest?: string
}

const OptionGroup = memo<IOptionGroupProps>(
  ({ dataTest, groupKey, groupOptions, label, onOptionSelection, optionType }) => (
    <div role='group' className='group' aria-label={label}>
      <p className='group-label'>{label}</p>

      {groupOptions.map((option: IOption) => (
        <Option
          key={`${groupKey}-option-${uuid()}`}
          option={option}
          optionType={optionType}
          onOptionSelection={onOptionSelection}
          dataTest={dataTest}
        />
      ))}
    </div>
  )
)

// List Box Component
interface IListBoxProps {
  isOpen: boolean
  isLoading: boolean
  listBoxID: string
  onOptionSelection(option: IOption): void
  options: IOptions
  style: {}
  dataTest?: string
}

const AutoCompleteListBox = forwardRef((props: IListBoxProps, ref: Ref<HTMLDivElement>) => {
  const {
    dataTest = '',
    isLoading,
    isOpen,
    listBoxID,
    onOptionSelection,
    options: { optionType, options },
    style,
  } = props

  return (
    <div
      role='listbox'
      className='search-results-container listbox'
      id={listBoxID}
      ref={ref}
      style={style || {}}
      data-test={dataTest}>
      {isOpen &&
        !isLoading &&
        (options as ISuggestionOption[]).map((option: ISuggestionOption) => {
          let component

          if (isOptionGroup(option)) {
            const groupKey = `autocomplete-${listBoxID}-group-${uuid()}`
            component = option.options.length ? (
              <OptionGroup
                key={groupKey}
                groupKey={groupKey}
                label={option.groupName}
                groupOptions={option.options}
                optionType={optionType}
                onOptionSelection={onOptionSelection}
                dataTest={dataTest}
              />
            ) : undefined
          } else {
            component = (
              <Option
                key={`autocomplete-${listBoxID}-option-${uuid()}`}
                option={option}
                optionType={optionType}
                onOptionSelection={onOptionSelection}
                dataTest={dataTest}
              />
            )
          }
          return component
        })}
      {debounceShowLoading(isLoading, isOpen) && (
        <div role='presentation'>
          <Spinner />
        </div>
      )}
    </div>
  )
})

export default AutoCompleteListBox
